import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, ExternalLink, Trash2 } from 'lucide-react';
import { useAuthStore, useArticlesStore, useLikedArticlesStore } from '../store/useStore';
import { social as socialApi, articles as articlesApi } from '../lib/api';
import { decodeHtmlEntities } from '../utils/decodeHtml';
import { formatAge, stamp } from '../utils/age';
import toast from 'react-hot-toast';

interface UserProfile {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  bio: string | null;
}

interface SharedArticle {
  id: string;
  note?: string;
  isPublic: boolean;
  createdAt: string;
  user: UserProfile;
  article: {
    id: string;
    title: string;
    excerpt?: string;
    url: string;
    imageUrl?: string;
    publishedAt?: string;
    feed: {
      title: string;
    };
    _count: {
      comments: number;
      shares: number;
    };
  };
}

export default function UserProfile() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const authUser = useAuthStore((state) => state.user)!;
  const updateArticle = useArticlesStore((state) => state.updateArticle);
  const { addLikedArticle, removeLikedArticle, isArticleLiked } = useLikedArticlesStore();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [sharedArticles, setSharedArticles] = useState<SharedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState<string | null>(null);

  useEffect(() => {
    async function loadUserShares() {
      if (!userId) return;

      try {
        const data = await socialApi.getUserShares(token, userId);
        setUser(data.user);
        setSharedArticles(data.shares);

        // Populate starred articles store from user shares
        data.shares.forEach(share => {
          if (share.article.isLiked) {
            addLikedArticle(share.article.id);
          }
        });
      } catch (error) {
        toast.error('Failed to load user shares');
        navigate('/');
      } finally {
        setLoading(false);
      }
    }

    loadUserShares();
  }, [userId, token, navigate, addLikedArticle]);

  async function handleToggleLike(articleId: string, isLiked: boolean) {
    try {
      if (isLiked) {
        await articlesApi.unlike(token, articleId);
        removeLikedArticle(articleId);
      } else {
        await articlesApi.like(token, articleId);
        addLikedArticle(articleId);
      }

      // Update the article in the shared articles list
      setSharedArticles(prev =>
        prev.map(share =>
          share.article.id === articleId
            ? { ...share, article: { ...share.article, isLiked: !isLiked } }
            : share
        )
      );

      // Also update the global articles store if the article exists there
      updateArticle(articleId, { isLiked: !isLiked });
    } catch (error) {
      toast.error('Failed to update article');
    }
  }

  async function handleUnshare(shareId: string) {
    try {
      await socialApi.deleteShare(token, shareId);
      setSharedArticles(prev => prev.filter(share => share.id !== shareId));
      setShowUnshareConfirm(null);
      toast.success('Article unshared');
    } catch (error) {
      toast.error('Failed to unshare article');
    }
  }

  if (loading) {
    return (
      <div className="pl-4 lg:pl-6 py-10">
        <span className="kicker">Loading…</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
        <div className="kicker mb-1">Not found</div>
        <p className="text-ink-secondary mb-4">That user doesn't exist.</p>
        <button onClick={() => navigate('/')} className="btn">Go back</button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-canvas page-transition">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex items-stretch border-b border-edge bg-canvas">
        <button onClick={() => navigate('/')} className="tool">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4 pl-4 lg:pl-6 pr-4 pt-8 pb-5 border-b border-edge">
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.username}
            className="w-14 h-14 rounded-full object-cover avatar-duotone flex-shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-ink text-paper flex items-center justify-center font-display font-extrabold text-xl flex-shrink-0">
            {user.username[0].toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="kicker mb-1">
            @{user.username} · {sharedArticles.length} shared
          </div>
          <h1 className="text-3xl">{user.displayName || user.username}</h1>
          {user.bio && (
            <p className="text-ink-secondary mt-2 max-w-prose">{user.bio}</p>
          )}
        </div>
      </div>

      {/* Shared articles */}
      <div>
        {sharedArticles.length === 0 ? (
          <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
            <div className="kicker mb-1">Empty</div>
            <p className="text-ink-secondary">Nothing shared yet.</p>
          </div>
        ) : (
          sharedArticles.map((share, index) => {
            const liked = isArticleLiked(share.article.id);
            return (
              <article
                key={share.id}
                onClick={() => navigate(`/article/${share.article.id}`)}
                className="grid grid-cols-[3.5rem_1fr] lg:grid-cols-[4.5rem_1fr] border-b border-edge-subtle cursor-pointer hover:bg-surface-sunken"
              >
                <div className="flex items-start pl-2 lg:pl-3 pr-2 py-3 border-r border-edge-subtle">
                  <span className="stamp">{stamp(index)}</span>
                </div>
                <div className="min-w-0 pl-3 lg:pl-4 pr-4 py-2.5 max-w-list">
                  <div className="kicker flex items-center gap-2">
                    <span className="truncate">
                      {share.article.feed.title}
                      {share.article.publishedAt && <> · {formatAge(share.article.publishedAt)}</>}
                      {' · shared '}{formatAge(share.createdAt)}
                      {share.isPublic && <> · public</>}
                    </span>
                    {share.user.id === authUser.id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowUnshareConfirm(share.id); }}
                        className="ml-auto p-1 text-ink-muted hover:text-signal"
                        title="Unshare this article"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <h2 className="font-sans text-[18px] font-semibold leading-snug tracking-normal text-ink line-clamp-2">
                    {decodeHtmlEntities(share.article.title)}
                  </h2>
                  {share.note && (
                    <p className="mt-1 text-sm text-ink-secondary border-l-2 border-ink pl-3">
                      {share.note}
                    </p>
                  )}
                  {share.article.excerpt && (
                    <p className="mt-1 text-sm text-ink-secondary leading-relaxed line-clamp-2">
                      {share.article.excerpt}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2 kicker">
                    <a
                      href={share.article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 hover:text-ink"
                    >
                      Source <ExternalLink className="w-3 h-3" />
                    </a>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleLike(share.article.id, liked); }}
                      className={`inline-flex items-center gap-1 hover:text-ink ${liked ? 'text-accent-600' : ''}`}
                      title={liked ? 'Unlike' : 'Like'}
                    >
                      <Heart className="w-3 h-3" fill={liked ? 'currentColor' : 'none'} />
                      {liked ? 'Liked' : 'Like'}
                    </button>
                    {share.article._count.comments > 0 && (
                      <span>{share.article._count.comments} {share.article._count.comments === 1 ? 'comment' : 'comments'}</span>
                    )}
                    {share.article._count.shares > 0 && (
                      <span>{share.article._count.shares} {share.article._count.shares === 1 ? 'share' : 'shares'}</span>
                    )}
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>

      {/* Unshare confirmation */}
      {showUnshareConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-animate">
          <div className="bg-canvas border border-ink p-6 w-full max-w-md mx-4 modal-animate">
            <div className="kicker mb-1">Unshare</div>
            <h3 className="text-xl mb-3">Remove this share?</h3>
            <p className="text-ink-secondary mb-6">This can't be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowUnshareConfirm(null)} className="btn btn-quiet">
                Cancel
              </button>
              <button onClick={() => handleUnshare(showUnshareConfirm)} className="btn btn-danger">
                Unshare
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
